import { createAsyncThunk } from "@reduxjs/toolkit";
import { baseUrl, headersDefault } from ".";
import axios from "axios";

// ── Throttle + inflight dedup for getMultimediaPosts ────────────────────
// Same pattern as getCourse/getPills in courseActions.js.
// Keyed by group_id so different modules don't block each other,
// but duplicate requests for the SAME group_id are collapsed.
const POSTS_THROTTLE_MS = 3_000; // 3 seconds
const postsInflight = new Map(); // group_id → Promise
const postsLastSuccess = new Map(); // group_id → timestamp

/** Call after actions that should force a fresh fetch (e.g. pill completion) */
export function resetMultimediaPostsThrottle(groupId) {
  if (groupId) {
    postsLastSuccess.delete(groupId);
    postsInflight.delete(groupId);
  } else {
    postsLastSuccess.clear();
    postsInflight.clear();
  }
}

export const getMultimediaPosts = createAsyncThunk(
  "multimediaGroups/getMultimediaPosts",
  async (functionParams) => {
    const { page = 1, group_id, search_title, tag_id } = functionParams ?? {};

    // If a request for this group_id is already in flight, piggyback on it
    const existing = postsInflight.get(group_id);
    if (existing) return existing;

    const promise = (async () => {
      try {
        const params = {
          page,
          group_id,
          ...(search_title && { search_title }),
          ...(tag_id && { tag_id }),
        };

        const response = await axios.get(
          `${baseUrl}/api/master-club-post-groups/list`,
          {
            headers: headersDefault(),
            params,
          },
        );

        postsLastSuccess.set(group_id, Date.now());
        return response.data;
      } finally {
        postsInflight.delete(group_id);
      }
    })();

    postsInflight.set(group_id, promise);
    return promise;
  },
  {
    // condition: return false to silently skip — no pending/fulfilled/rejected
    condition: (functionParams, { getState }) => {
      const { group_id } = functionParams ?? {};
      if (!group_id) return false;

      // Skip if data for this group was fetched recently
      const lastSuccess = postsLastSuccess.get(group_id) || 0;
      if (Date.now() - lastSuccess < POSTS_THROTTLE_MS) {
        // Only skip if we already have data in Redux for this group
        const state = getState();
        const currentGroupId =
          state.multimediaGroups?.getMultimediaPosts?.data?.group?.id;
        if (currentGroupId === group_id) return false;
      }

      // Skip if already loading this exact group
      if (postsInflight.has(group_id)) return false;

      return true;
    },
  },
);

export const getMultimediaGroupsList = createAsyncThunk(
  "multimediaGroups/getMultimediaGroupsList",
  async () => {
    const params = { type: "multimedia" };

    const response = await axios.get(`${baseUrl}/api/master-club-groups/list`, {
      headers: headersDefault(),
      params,
    });

    return response.data;
  },
);

export const likeMultimediaPost = createAsyncThunk(
  "multimediaGroups/likeMultimediaPost",
  async ({ element_id }) => {
    const body = {
      element_type: "App\\Models\\MasterClubPostGroup",
      element_id,
    };

    const response = await axios.post(
      `${baseUrl}/api/master-club-likes`,
      body,
      {
        headers: headersDefault(),
      },
    );

    return response.data;
  },
);
